'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { BlogCard } from '@/features/blog/components/blog-card';
import type { BlogPost } from '@/types/content';

interface BlogListProps {
  posts: BlogPost[];
  tags: string[];
}

export function BlogList({ posts, tags }: BlogListProps) {
  const t = useTranslations('Blog');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const filteredPosts = selectedTag
    ? posts.filter((post) =>
        post.tags.some((tag) => tag.toLowerCase() === selectedTag.toLowerCase()),
      )
    : posts;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-24 md:py-32">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto text-center mb-20 px-4 md:px-8 lg:px-10">
        <Badge
          variant="outline"
          className="mb-6 px-4 py-1.5 text-sm border-primary/20 bg-primary/5 text-primary"
        >
          <BookOpen className="w-3.5 h-3.5 mr-2" />
          {t('badge')}
        </Badge>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-foreground text-balance">
          {t('title')}
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed text-balance">
          {t('subtitle')}
        </p>

        {tags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            <AnimatePresence>
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'secondary'}
                  className={`px-3 py-1 text-sm cursor-pointer transition-all capitalize ${
                    selectedTag === tag
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-muted/50 hover:bg-muted'
                  }`}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                >
                  {tag}
                  {selectedTag === tag && <X className="w-3 h-3 ml-1.5" />}
                </Badge>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Blog Grid */}
      <div className="min-h-[400px]">
        {filteredPosts.length > 0 ? (
          <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredPosts.map((post) => (
                <motion.div
                  layout
                  key={post.slug}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24 bg-muted/30 rounded-3xl border border-border/50"
          >
            <p className="text-xl text-muted-foreground">No blog posts found for this category.</p>
            <button
              onClick={() => setSelectedTag(null)}
              className="mt-4 text-primary hover:underline text-sm font-medium"
            >
              Clear filter
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
